import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react'
import { useAuth } from '../../hooks/useAuth'
import { useBalance } from '../../hooks/useBalance'
import Header from './Header'
import { TabBar } from '../ui'
import ButterflyLoader from '../ui/ButterflyLoader'
import type { Tab } from '../ui/TabBar'
import type { Wallet, WalletInfoWithOpenMethod } from '@tonconnect/ui'
import type { BalanceInfo } from '../../lib/api'

// Context type for child pages
export interface AppContext {
  balance: BalanceInfo | null
  refreshBalance: () => void
  apiKey: string
  /** Store the first (or replacement) API key after manual creation */
  setFirstKey: (address: string, key: string) => void
  /** Connected wallet — null when user hasn't connected yet */
  wallet: Wallet | (Wallet & WalletInfoWithOpenMethod) | null
}

/** Branded splash — shown while TonConnect restores wallet state from storage */
function AppSplash() {
  return (
    <div
      className="flex flex-col flex-1 items-center justify-center"
      style={{ backgroundColor: 'var(--tg-theme-bg-color)' }}
    >
      <ButterflyLoader size={120} />
    </div>
  )
}

/**
 * DEV PREVIEW: skip auth when ?preview=1 is set once — persists for the session.
 * Stored in sessionStorage so it survives React Router navigations.
 */
const DEV_PREVIEW = (() => {
  if (!import.meta.env.DEV) return false
  if (new URLSearchParams(window.location.search).get('preview') === '1') {
    sessionStorage.setItem('imago-preview', '1')
    return true
  }
  return sessionStorage.getItem('imago-preview') === '1'
})()

export default function AppLayout() {
  const wallet = useTonWallet()
  const [tonConnectUI] = useTonConnectUI()
  const { apiKey, setFirstKey } = useAuth()
  const tonAddress = wallet?.account.address
  const { balance, refresh: refreshBalance } = useBalance(apiKey, tonAddress)
  const location = useLocation()

  /**
   * isInitializing: true until TonConnect has finished restoring the session
   * from localStorage. We use `connectionRestored` (a Promise that resolves
   * after TonConnect reads its stored data) instead of `onStatusChange`,
   * which can fire *before* the wallet is actually restored — causing a
   * brief flash of ConnectPage even when the user has a connected wallet.
   * Fallback timeout: 1s guards against edge cases where the Promise stalls.
   */
  const [isInitializing, setIsInitializing] = useState(true)

  useEffect(() => {
    let active = true
    const done = () => { if (active) setIsInitializing(false) }

    // connectionRestored resolves once TonConnect finishes reading localStorage.
    // We delay by one animation frame so useTonWallet() has a chance to sync
    // its React state — without this, wallet can be null for one render after
    // connectionRestored fires, causing a brief flash/redirect to ConnectPage.
    tonConnectUI.connectionRestored.then(() => {
      requestAnimationFrame(() => done())
    })

    // Safety fallback — should rarely trigger
    const fallback = setTimeout(done, 1000)

    return () => {
      active = false
      clearTimeout(fallback)
    }
  }, [tonConnectUI])

  // TonConnect scroll-lock fix
  useEffect(() => {
    const unsubscribe = tonConnectUI.onStatusChange((connectedWallet) => {
      if (connectedWallet) {
        document.body.classList.remove('tc-disable-scroll')
        document.body.style.top = 'auto'
        document.body.style.position = ''
        tonConnectUI.closeModal()
      }
    })
    return unsubscribe
  }, [tonConnectUI])

  // Show splash while TonConnect is restoring state — prevents flash of wrong UI
  if (!DEV_PREVIEW && isInitializing) {
    return <AppSplash />
  }

  // Full-screen pages — render their own header, no tab bar
  const isChatSession = /^\/chat\/.+/.test(location.pathname)
  const isFullScreen = isChatSession
    || location.pathname === '/deposit'

  // Determine active tab from URL
  const activeTab: Tab = location.pathname.startsWith('/chat')
    ? 'chat'
    : location.pathname.startsWith('/models') || location.pathname === '/compare'
      ? 'models'
      : 'home'

  const ctx: AppContext = { balance, refreshBalance, apiKey: apiKey ?? '', setFirstKey, wallet: wallet ?? null }

  return (
    <div
      className="flex flex-col flex-1 min-h-0"
      style={{ backgroundColor: 'var(--tg-theme-bg-color)' }}
    >
      {!isFullScreen && <Header balance={balance} />}

      <main
        className="flex flex-col flex-1 min-h-0"
        style={{
          paddingTop: isFullScreen ? '0' : 'var(--header-height)',
          paddingBottom: isFullScreen ? '0' : 'calc(var(--tab-bar-height) + var(--safe-bottom))',
        }}
      >
        <Outlet context={ctx} />
      </main>

      {!isFullScreen && <TabBar activeTab={activeTab} />}
    </div>
  )
}
